import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Table, Button, Input, Select, Space, Tag, Modal, Dropdown, Menu, message } from 'antd';
import { PlusOutlined, SearchOutlined, FilterOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAssets, useAssetActions } from '../../../hooks/useAssets';
import { Asset, AssetStatus } from '../../../types/asset.types';
import { all_routes } from '../../../router/all_routes';
import ImageWithBasePath from '../../../core/common/imageWithBasePath';
import CreateAssetModal from './components/CreateAssetModal';
import UpdateAssetModal from './components/UpdateAssetModal';

const { Option } = Select;
const { confirm } = Modal;

const AssetsPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  
  const {
    assets,
    filteredAssets,
    employees,
    loading,
    error,
    selectedStatus,
    selectedEmployee,
    searchTerm,
    fetchAssets,
    fetchEmployees,
    handleStatusChange,
    handleEmployeeChange,
    handleSearchChange,
    resetFilters
  } = useAssets();

  const { deleteAsset } = useAssetActions();

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  useEffect(() => {
    fetchAssets();
    fetchEmployees();
  }, [fetchAssets, fetchEmployees]);

  const handleDelete = async (assetId: number) => {
    try {
      await deleteAsset(assetId);
      fetchAssets(); // Listeyi yenile
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const showDeleteConfirm = (asset: Asset) => {
    confirm({
      title: 'Zimmeti silmek istediğinize emin misiniz?',
      icon: <ExclamationCircleOutlined />,
      content: `${asset.name} (${asset.serialNumber}) kalıcı olarak silinecek.`,
      okText: 'Evet, Sil',
      okType: 'danger',
      cancelText: 'İptal',
      onOk() {
        handleDelete(asset.id);
      },
    });
  };

  const handleStatusUpdate = (asset: Asset) => {
    setSelectedAsset(asset);
    setUpdateModalVisible(true);
  };

  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.ASSIGNED:
        return 'processing';
      case AssetStatus.RETURNED:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.ASSIGNED:
        return 'Atanmış';
      case AssetStatus.RETURNED:
        return 'İade Edildi';
      default:
        return status;
    }
  };

  const getEmployeeName = (userId: number) => {
    const employee = employees.find(emp => emp.id === userId);
    return employee ? `${employee.firstName} ${employee.lastName}` : 'Bilinmeyen';
  };

  const actionMenu = (record: Asset) => (
    <Menu>
      <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleStatusUpdate(record)}>
        Durumu Güncelle
      </Menu.Item>
      <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => showDeleteConfirm(record)} danger>
        Sil
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: 'Zimmet Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Asset, b: Asset) => a.name.localeCompare(b.name),
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      sorter: (a: Asset, b: Asset) => a.serialNumber.localeCompare(b.serialNumber),
    },
    {
      title: 'Çalışan',
      dataIndex: 'userId',
      key: 'userId',
      render: (userId: number) => getEmployeeName(userId),
      sorter: (a: Asset, b: Asset) => getEmployeeName(a.userId).localeCompare(getEmployeeName(b.userId)),
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
      sorter: (a: Asset, b: Asset) => a.status.localeCompare(b.status),
    },
    {
      title: 'Zimmet Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
      sorter: (a: Asset, b: Asset) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'İade Tarihi',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date: string, record: Asset) => {
        if (record.status === AssetStatus.RETURNED) {
          return new Date(date).toLocaleDateString('tr-TR');
        }
        return '-';
      },
      sorter: (a: Asset, b: Asset) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (text: any, record: Asset) => (
        <Dropdown overlay={actionMenu(record)} trigger={['click']}>
          <Button type="link">İşlemler</Button>
        </Dropdown>
      ),
    },
  ];

  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    fetchAssets();
    message.success('Zimmet başarıyla oluşturuldu');
  };

  const handleUpdateSuccess = () => {
    setUpdateModalVisible(false);
    setSelectedAsset(null);
    fetchAssets();
    message.success('Zimmet durumu güncellendi');
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between page-header-title">
          <h4 className="fw-medium mb-0">Zimmet Yönetimi</h4>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to={routes.adminDashboard}>Anasayfa</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Zimmet Yönetimi
              </li>
            </ol>
          </nav>
        </div>

        {/* Filters */}
        <div className="card">
          <div className="card-body">
            <div className="row g-3 align-items-center">
              <div className="col-md-3">
                <Input
                  placeholder="Zimmet adı veya seri numarası ara..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  allowClear
                />
              </div>
              <div className="col-md-3">
                <Select
                  placeholder="Durum seçin"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value="all">Tüm Durumlar</Option>
                  <Option value={AssetStatus.ASSIGNED}>Atanmış</Option>
                  <Option value={AssetStatus.RETURNED}>İade Edildi</Option>
                </Select>
              </div>
              <div className="col-md-3">
                <Select
                  placeholder="Çalışan seçin"
                  value={selectedEmployee}
                  onChange={handleEmployeeChange}
                  style={{ width: '100%' }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value="all">Tüm Çalışanlar</Option>
                  {employees.map(employee => (
                    <Option key={employee.id} value={employee.id.toString()}>
                      {employee.firstName} {employee.lastName}
                    </Option>
                  ))}
                </Select>
              </div>
              <div className="col-md-3">
                <Space>
                  <Button icon={<FilterOutlined />} onClick={resetFilters}>
                    Filtreleri Temizle
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    Yeni Zimmet
                  </Button>
                </Space>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="row">
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-primary rounded-circle">
                      <i className="ti ti-archive text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Toplam Zimmet</h6>
                    <h4 className="mb-0">{assets.length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-warning rounded-circle">
                      <i className="ti ti-user-check text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Atanmış</h6>
                    <h4 className="mb-0">{assets.filter(a => a.status === AssetStatus.ASSIGNED).length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-success rounded-circle">
                      <i className="ti ti-check text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">İade Edildi</h6>
                    <h4 className="mb-0">{assets.filter(a => a.status === AssetStatus.RETURNED).length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-danger rounded-circle">
                      <i className="ti ti-alert-circle text-white fs-16"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-1">Bekleyen İade</h6>
                    <h4 className="mb-0">{assets.filter(a => a.status === AssetStatus.ASSIGNED).length}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body">
            <Table
              columns={columns}
              dataSource={filteredAssets}
              loading={loading}
              rowKey="id"
              pagination={{
                total: filteredAssets.length,
                pageSize: 10,
                showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
              }}
            />
          </div>
        </div>

        {/* Modals */}
        <CreateAssetModal
          visible={createModalVisible}
          onClose={() => setCreateModalVisible(false)}
          onSuccess={handleCreateSuccess}
          employees={employees}
        />

        <UpdateAssetModal
          visible={updateModalVisible}
          onClose={() => setUpdateModalVisible(false)}
          onSuccess={handleUpdateSuccess}
          asset={selectedAsset}
        />
      </div>
    </div>
  );
};

export default AssetsPage; 